import { Pill } from "../lib/primitives";
import { AreaChart } from "../lib/charts";
import { fmt } from "../lib/format";
import { useAnalytics, useBilling, useUsage } from "../lib/hooks";

export function Billing() {
  const { data: billing } = useBilling();
  const { data: usage } = useUsage();
  const { data: analytics } = useAnalytics();

  const planName = billing?.plan?.name ?? "—";
  const included = billing?.plan?.monthlyLimit ?? 0;
  const pricePerMonth = billing?.plan?.pricePerMonth ?? 0;
  const used = usage?.authVolume?.total ?? 0;
  const pct = included ? (used / included) * 100 : 0;
  const series = analytics?.series ?? [];

  return (
    <>
      <div className="ad-page-head">
        <div className="ad-page-head-l">
          <div className="ad-eyebrow">Account</div>
          <h1 className="ad-h1">Billing</h1>
          <div className="ad-sub">You pay for each successful sign-in. Failed ones are free.</div>
        </div>
      </div>

      <div className="ad-grid-2" style={{ marginBottom: 18 }}>
        <div className="ad-card-hot">
          <div className="ad-card-head"><div className="ad-card-title">This billing period</div><Pill kind="orange">{planName}</Pill></div>
          <div style={{ fontSize: 32, fontWeight: 500, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums", color: "var(--ad-fg-1)" }}>{fmt(used)}</div>
          <div style={{ fontSize: 12, color: "var(--ad-fg-3)" }}>successful sign-ins this billing period</div>
          <div style={{ height: 14 }} />
          <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "var(--ad-fg-3)", marginBottom: 4 }}>
            <span>{fmt(used)} of {fmt(included)} included</span>
            <span className="ad-mono-or">{Math.round(pct)}%</span>
          </div>
          <div className="ad-progress"><div className="ad-progress-bar" style={{ width: `${Math.min(pct, 100)}%` }} /></div>
          <div style={{ fontSize: 11, color: "var(--ad-fg-3)", marginTop: 6 }}>
            {planName} plan{pricePerMonth ? ` · $${pricePerMonth}/mo` : " · free"}. Usage-based billing isn't enabled yet.
          </div>
        </div>
        <div className="ad-card">
          <div className="ad-card-head"><div className="ad-card-title">Payment method</div><Pill kind="warn" dot>Coming soon</Pill></div>
          <div className="ad-stack-md">
            <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", background: "var(--ad-surface-3)", borderRadius: 10, opacity: 0.75 }}>
              <div style={{ width: 36, height: 24, background: "linear-gradient(135deg, #1A1F71, #5B65D8)", borderRadius: 4, display: "grid", placeItems: "center", fontSize: 9, fontWeight: 800, color: "#fff", letterSpacing: 0.1 }}>VISA</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "var(--rb-font-mono)", fontSize: 13, color: "var(--ad-fg-2)" }}>•••• •••• •••• ••••</div>
                <div style={{ fontSize: 11, color: "var(--ad-fg-3)" }}>No card on file</div>
              </div>
            </div>
            <div style={{ fontSize: 11, color: "var(--ad-fg-3)" }}>
              You're on the {planName} plan{pricePerMonth ? "" : " (free)"}. Card management and usage-based billing arrive with paid plans.
            </div>
          </div>
        </div>
      </div>

      <div className="ad-card" style={{ marginBottom: 18 }}>
        <div className="ad-card-head">
          <div className="ad-card-title">Usage</div>
          <div style={{ fontSize: 14, color: "var(--ad-fg-3)" }}>Last 14 days</div>
        </div>
        {series.length > 0 ? (
          <AreaChart data={series.slice(-14)} height={180} valueKey="total" labelKey="date" />
        ) : (
          <div className="ad-empty" style={{ border: 0, margin: 0 }}>
            <div className="ad-empty-title">No usage yet</div>
            <div className="ad-empty-sub">Successful sign-ins show up here once your app is live.</div>
          </div>
        )}
      </div>

      <div className="ad-card-flat" style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--ad-hairline)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div className="ad-card-title">Invoices</div>
          <Pill kind="warn" dot>Coming soon</Pill>
        </div>
        <div className="ad-empty" style={{ border: 0, margin: 0 }}>
          <div className="ad-empty-title">No invoices yet</div>
          <div className="ad-empty-sub">Invoices show up here once billing starts.</div>
        </div>
      </div>
    </>
  );
}
